import { Component } from '@theme/component';

const STORAGE_KEY = 'newsletter_popup_closed';
const SUBSCRIBED_KEY = 'newsletter_popup_subscribed';

/**
 * Newsletter Popup Form Component
 * Handles AJAX form submission and state management
 */
class NewsletterPopupFormComponent extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    // Check if we just returned from a successful submission
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('newsletter_popup_success') === 'true') {
      // Show success state
      this.#handleSuccessReturn();

      // Clean URL (remove success parameter)
      const url = new URL(window.location);
      url.searchParams.delete('newsletter_popup_success');
      window.history.replaceState({}, '', url.toString());
    }

    // Add form validation
    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('submit', this.#handleSubmit.bind(this), { signal: this.#abortController.signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #handleSubmit(event) {
    const form = event.target;
    const emailInput = form.querySelector('input[type="email"]');

    if (!emailInput) return;

    const email = emailInput.value.trim();
    if (!email) {
      event.preventDefault();
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      event.preventDefault();
      this.#showError('Voer een geldig e-mailadres in.');
      return;
    }

    // Clear any previous errors
    this.#hideError();

    // Show loading state
    const submitButton = form.querySelector('button[type="submit"]');
    if (submitButton) {
      this.#showLoading(submitButton);
    }

    // Let form submit normally (page reload will happen)
    // Shopify will redirect back with newsletter_popup_success=true
  }

  #handleSuccessReturn() {
    // Open the popup
    const dialogComponent = this.closest('dialog-component');
    if (dialogComponent && dialogComponent.showDialog) {
      dialogComponent.showDialog();
    }

    // Show success message
    this.#showSuccess();

    // Set 365-day cookie
    this.#setSubscribedCookie();

    // Close popup after 5 seconds
    setTimeout(() => {
      this.#closePopup();
    }, 5000);
  }

  #showLoading(button) {
    button.disabled = true;
    button.dataset.originalText = button.textContent;
    button.textContent = 'Bezig...';
  }

  #hideLoading(button) {
    button.disabled = false;
    if (button.dataset.originalText) {
      button.textContent = button.dataset.originalText;
    }
  }

  #showSuccess() {
    // Hide form fields
    const formFields = this.querySelector('.newsletter-popup-form__description, .newsletter-popup-form__field, .newsletter-popup-form__submit, .newsletter-popup-form__disclaimer');
    if (formFields) {
      this.querySelectorAll('.newsletter-popup-form__description, .newsletter-popup-form__field, .newsletter-popup-form__submit, .newsletter-popup-form__disclaimer').forEach(el => {
        el.style.display = 'none';
      });
    }

    // Show or create success message
    let successMessage = this.querySelector('.newsletter-popup-form__success');
    if (!successMessage) {
      successMessage = document.createElement('div');
      successMessage.className = 'newsletter-popup-form__message newsletter-popup-form__success';
      successMessage.setAttribute('role', 'status');
      successMessage.setAttribute('aria-live', 'polite');

      const successText = this.dataset.successMessage || 'Bedankt voor je inschrijving! Check je inbox voor je kortingscode.';

      successMessage.innerHTML = `
        <span class="svg-wrapper">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
          </svg>
        </span>
        <span>${successText}</span>
      `;

      const form = this.querySelector('form');
      form?.appendChild(successMessage);
    }

    successMessage.style.display = 'flex';
  }

  #showError(message) {
    // Hide any existing error first
    this.#hideError();

    let errorMessage = this.querySelector('.newsletter-popup-form__error');
    if (!errorMessage) {
      errorMessage = document.createElement('div');
      errorMessage.className = 'newsletter-popup-form__message newsletter-popup-form__error';
      errorMessage.setAttribute('role', 'alert');
      errorMessage.setAttribute('aria-live', 'assertive');

      const submitButton = this.querySelector('button[type="submit"]');
      submitButton?.parentNode?.insertBefore(errorMessage, submitButton);
    }

    errorMessage.innerHTML = `
      <span class="svg-wrapper">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
        </svg>
      </span>
      <span>${message}</span>
    `;

    errorMessage.style.display = 'flex';
  }

  #hideError() {
    const errorMessage = this.querySelector('.newsletter-popup-form__error');
    if (errorMessage) {
      errorMessage.style.display = 'none';
    }
  }

  #closePopup() {
    const dialogComponent = this.closest('dialog-component');
    if (dialogComponent && dialogComponent.closeDialog) {
      dialogComponent.closeDialog();
    }
  }

  #setSubscribedCookie() {
    try {
      const timestamp = new Date().getTime();
      const expiryData = {
        timestamp: timestamp,
        subscribed: true,
        expiryDays: 365
      };
      localStorage.setItem(SUBSCRIBED_KEY, JSON.stringify(expiryData));
    } catch (e) {
      console.warn('Could not save subscription state:', e);
    }
  }
}

if (!customElements.get('newsletter-popup-form-component')) {
  customElements.define('newsletter-popup-form-component', NewsletterPopupFormComponent);
}

/**
 * Newsletter Popup Manager
 * Handles showing the popup based on timing and user preferences
 */
class NewsletterPopupManager {
  constructor() {
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    const wrapper = document.querySelector('.newsletter-popup-wrapper');
    if (!wrapper) return;

    const showDelay = parseInt(wrapper.dataset.showDelay) || 5000;
    const daysUntilReshow = parseInt(wrapper.dataset.daysUntilReshow) || 30;

    // Check if user has already subscribed - if yes, NEVER show popup again
    if (this.hasSubscribed()) {
      return;
    }

    // Check if we should show the popup based on close timestamp
    if (!this.shouldShowPopup(daysUntilReshow)) {
      return;
    }

    // Show popup after delay
    setTimeout(() => {
      this.showPopup(wrapper);
    }, showDelay);
  }

  hasSubscribed() {
    try {
      const subscribedData = localStorage.getItem(SUBSCRIBED_KEY);
      if (!subscribedData) return false;

      const data = JSON.parse(subscribedData);

      // Check if subscription is still valid (365 days)
      const now = new Date().getTime();
      const daysSinceSubscribed = (now - data.timestamp) / (1000 * 60 * 60 * 24);

      return data.subscribed && daysSinceSubscribed < data.expiryDays;
    } catch (e) {
      return false;
    }
  }

  shouldShowPopup(daysUntilReshow) {
    try {
      const lastClosed = localStorage.getItem(STORAGE_KEY);
      if (!lastClosed) return true;

      const lastClosedTime = parseInt(lastClosed);
      const now = new Date().getTime();
      const daysSinceClosed = (now - lastClosedTime) / (1000 * 60 * 60 * 24);

      return daysSinceClosed >= daysUntilReshow;
    } catch (e) {
      // If localStorage is not available, always show
      return true;
    }
  }

  showPopup(wrapper) {
    if (wrapper && wrapper.showDialog) {
      wrapper.showDialog();
    }
  }

  setClosedTimestamp() {
    try {
      const timestamp = new Date().getTime();
      localStorage.setItem(STORAGE_KEY, timestamp.toString());
    } catch (e) {
      console.warn('Could not save popup state:', e);
    }
  }
}

// Initialize the popup manager
new NewsletterPopupManager();
